import { useEffect } from "react";

/**
 * Shared scroll behaviour for the marketing pages:
 *  - reveals `.reveal` sections once when they scroll into view (adds `.play`)
 *  - lifts the hero panel slightly as it enters (parallax)
 *  - fades/scales the first sticky card back as the second one takes over
 *
 * All of it is skipped when the visitor prefers reduced motion; the CSS in
 * marketing.css already renders every animated element in its finished state.
 *
 * `ready` must be false for as long as the page is still rendering its auth
 * skeleton. The effect queries the DOM once, so running it against the
 * skeleton finds no `.reveal` sections, observes nothing, and leaves every
 * `.play`-gated element (`.feat`, `.r-card`, `.up-doc`, `.tr-chip`) stuck at
 * opacity 0 for the life of the page.
 */
export function useMarketingMotion(ready = true) {
  useEffect(() => {
    if (!ready) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const reveals = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));

    if (reduced) {
      reveals.forEach((el) => el.classList.add("play"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("play");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );
    reveals.forEach((el) => observer.observe(el));

    const panel = document.getElementById("heroPanel");
    const stackTop = document.getElementById("stackTop");
    const stackBottom = document.getElementById("stackBottom");
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const viewportHeight = window.innerHeight;

        if (panel) {
          const rect = panel.getBoundingClientRect();
          const progress = Math.min(
            Math.max((viewportHeight - rect.top) / (viewportHeight + rect.height), 0),
            1
          );
          panel.style.transform = `translateY(${(1 - progress) * 46}px)`;
        }

        if (stackTop && stackBottom) {
          const rect = stackBottom.getBoundingClientRect();
          const takeover = Math.min(
            Math.max(1 - (rect.top - 74) / (viewportHeight * 0.55), 0),
            1
          );
          stackTop.style.transform = `scale(${1 - takeover * 0.05})`;
          stackTop.style.opacity = String(1 - takeover * 0.35);
        }

        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [ready]);
}
